import { z } from 'zod';

const GridXY = z.object({
  x: z.number().int().nonnegative(),
  y: z.number().int().nonnegative(),
});

export const ColorSchema = z.enum(['R', 'G', 'B', 'W']);
export type Color = z.infer<typeof ColorSchema>;

const PrimaryColor = z.enum(['R', 'G', 'B']);

/**
 * Level layout. Slots are *generic* placement points — at runtime the player
 * picks which device (mirror or prism) goes in each one, constrained by the
 * level's `mirrors.available` and `prisms.available` budgets.
 */
export const LevelSpecSchema = z.object({
  id: z.string().min(1),
  title: z.string(),
  grid: z.object({
    width: z.number().int().positive(),
    height: z.number().int().positive(),
  }),
  optimalItems: z.number().int().nonnegative(),
  showAngleHelper: z.boolean(),
  showPrismHelper: z.boolean().optional().default(false),
  lightSources: z
    .array(
      z.object({
        pos: GridXY,
        direction: z.enum(['N', 'S', 'E', 'W']),
        color: ColorSchema,
      }),
    )
    .min(1),
  mirrors: z.object({ available: z.number().int().nonnegative() }),
  prisms: z
    .object({ available: z.number().int().nonnegative() })
    .optional()
    .default({ available: 0 }),
  /** Every slot is generic — the picker decides what device goes in it. */
  slots: z.array(GridXY),
  switches: z.array(
    z.object({
      pos: GridXY,
      id: z.string().min(1),
      requires: z.array(PrimaryColor).optional().default([]),
    }),
  ),
  exit: z.object({ pos: GridXY, requires: z.array(z.string()) }),
  walls: z.array(GridXY).optional(),
});

export type LevelSpec = z.infer<typeof LevelSpecSchema>;
